import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import axios from "axios";
import { Box, Divider } from "@mui/material";
import {
    Button,
    DataTable,
    DataTableCell,
    DataTableContainer,
    DataTableHeaderCell,
    Text,
    FlexContainer,
    LottieLoadingIndicator,
    StandardModal,
    CustomSelect
} from "src/components";
import { surveyUserGroupOptions, EnumSurveyType, EnumUserGroup, getQuestionSurveyType } from "../components/Enums";
import { Toast } from "src/components";
import { SurveyStatusType, InsightSortableColumns } from "src/constants/survey";
import { API_URL } from "src/scenes/App";
import { UserContext } from "src/scenes/App/UserContext";

//assets
import ArrowDown from "src/img/ArrowDown.svg";
import ArrowUp from "src/img/ArrowDown.svg";
import IconExcellent from "src/img/survey/updatedIcons/Excellent@2x.png";
import IconGreat from "src/img/survey/updatedIcons/Great@2x.png";
import IconOk from "src/img/survey/updatedIcons/ok@2x.png";
import IconPoor from "src/img/survey/updatedIcons/poor@2x.png";
import IconBad from "src/img/survey/updatedIcons/bad@2x.png";

const Th = styled(DataTableHeaderCell)`
    font-weight: 500;
    width: ${p => p.width};
    min-width: ${p => p.minWidth };
    text-align: ${p => p.textAlign};
    flex-direction: row;
    justify-content: left;
    font-size: 16px;
    user-select: none;
`;

const Td = styled(DataTableCell)`
    text-align: center;
    font-size: 16px;
    font-weight: ${p => p.fontWeight};
    padding: ${p => p.padding};
`;

const Tr = styled.tr`
    width: 100%;
    height: 70px;
    text-align: center;
    font-size: 16px;
    font-weight: ${p => p.fontWeight};
    border: 1px solid #D8DEEC;
`;

const UserInitials = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #d0d7de;
    color: #4f4f4f;
    font-weight: bold;
    font-size: 14px;
    margin-right: 10px;
    cursor: default;
    user-select: none;
`;

const UserPhoto = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-image: url(${p => p.image});
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    margin-right: 10px;
    cursor: none;
`;

const SurveyScrollableDiv = styled(DataTableContainer)`
    max-height: 24rem;
    overflow: auto;
    width: 100%;
`;

const SurveyUserSubmissionsModal = (props) => {
    const { showDialog, handleClose, insights, survey } = props;
    const [selectedSurveyStatusType, setSelectedSurveyStatusType] = useState(survey.userGroup);
    const [surveyUserSubmissionList, setSurveyUserSubmissionList] = useState([]);
    const [sortColumn, setSortColumn] = useState(InsightSortableColumns.STATUS);
    const [sortDirection, setSortDirection] = useState("asc");
    const [isSurveyResponsesFetched, setIsSurveyResponsesFetched] = useState(false);
    const currentUser = useContext(UserContext);
    const headers = { headers: { Authorization: "Bearer " + currentUser.token } };

    const sortData = (column) => {
        const direction = sortColumn === column && sortDirection === "asc" ? "desc" : "asc";
        setSortColumn(column);
        setSortDirection(direction);

        const sortByName = (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());

        const sortByColumn = (a, b) => {
            let aValue = "";
            let bValue = "";

            switch (column) {
                case InsightSortableColumns.NAME:
                    aValue = a.name;
                    bValue = b.name;
                    break;
                case InsightSortableColumns.EMAIL:
                    aValue = a.email;
                    bValue = b.email;
                    break;
                case InsightSortableColumns.QUESTION_BY_INDEX:
                    console.warn("TODO: Sort question by question column correctly");
                    aValue = a.questionResponses;
                    bValue = b.questionResponses;
                    break;
                case InsightSortableColumns.STATUS:
                    aValue = a.status;
                    bValue = b.status;
                    if (aValue === bValue) {
                        return sortByName(a, b);
                    }
                    break;
                default:
                    return 0;
            }

            if (!aValue) return direction === "asc" ? 1 : -1;
            if (!bValue) return direction === "asc" ? -1 : 1;

            if (aValue < bValue) {
                return direction === "asc" ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === "asc" ? 1 : -1;
            }
            return 0;
        };

        if (column === InsightSortableColumns.STATUS) {
            
            const usersWithResponses = surveyUserSubmissionList.filter(user =>
                user.questionResponses
            );
    
            const usersWithoutResponses = surveyUserSubmissionList.filter(user =>
                !user.questionResponses
            );
    
            const sortedWithResponses = [...usersWithResponses].sort(sortByColumn);
            const sortedWithoutResponses = [...usersWithoutResponses].sort(sortByColumn);
    
            const sortedData = direction === "asc" 
                ? [...sortedWithResponses.sort(sortByName), ...sortedWithoutResponses.sort(sortByName)] 
                : [...sortedWithoutResponses.sort(sortByName), ...sortedWithResponses.sort(sortByName)];
            setSurveyUserSubmissionList(sortedData);
        } else {
            const sortedData = [...surveyUserSubmissionList].sort(sortByColumn);
            setSurveyUserSubmissionList(sortedData);
        }
    };

    const renderSortIndicator = (column) => {
        if (sortColumn === column) {
            return (sortDirection === "asc" ? (
                <img src={ArrowUp} alt="SortArrow" style={{ transform: "rotate(180deg)", filter: "brightness(0) saturate(100%)", width: "10px" }} />
            ) : (
                <img src={ArrowDown} alt="SortArrow" style={{ filter: "brightness(0) saturate(100%)", width: "10px" }} />
            ));
        }
        return null;
    };

    const handleSurveyTypeIcons = (surveyType, questionResponse) => {
        const satisfactionIconsList = {
            excellent: IconExcellent,
            great: IconGreat,
            ok: IconOk,
            poor: IconPoor,
            bad: IconBad,
        };
    
        if (surveyType === EnumSurveyType.SATISFACTION) {
            const Icon = satisfactionIconsList[questionResponse.answerChosen];
            return Icon ? <img src={Icon} alt={Icon} height="36px" width="36px" /> : null;
        }
    
        return questionResponse.answerChosen || questionResponse.feedbackMessage || "-";
    };

    const emptyList = (survey, existingRowCount) => {
        const emptyRows = 10 - existingRowCount;

        if (emptyRows < 1) {
            return null; // Or return an empty array: []
        }
      
        return Array.from({ length: emptyRows }, (_, i) => (
            <Tr key={"empty_" + i} className="placeholder-row">
                <Td $empty>&nbsp;</Td>
                <Td $empty>&nbsp;</Td>
                <Td $empty>&nbsp;</Td>
                {survey.surveyQuestions.map((_, questionIndex) => (
                    <Td key={`empty_question_cell_${questionIndex}`} $empty>&nbsp;</Td>
                ))}
            </Tr>
        ));
    };

    const surveyUserSubmissionListItems = (surveyUserSubmissionList) => surveyUserSubmissionList.map((surveyUserResponse, index) => {
        return <Tr key={"ResponseListItems_" + index}>
            <Td style={{ textAlign: "left", paddingLeft: "20px", width: "30%" }} fontWeight="300 !important">
                {
                    !surveyUserResponse.profilePicture ?
                        <>
                            <UserInitials>
                                { surveyUserResponse.firstName[0] }
                                { surveyUserResponse.lastName[0] }
                            </UserInitials>
                        </> :
                        <>
                            <UserPhoto image={surveyUserResponse.profilePicture} >
                                &nbsp;
                            </UserPhoto>
                        </>

                }
                <span style={{ margin: "auto" }}>
                    {surveyUserResponse.name}
                </span>
            </Td>
            <Td style={{ textAlign: "left", paddingLeft: "20px", width: "20%" }} fontWeight="300 !important">
                {surveyUserResponse.email}
            </Td>
            {
                surveyUserResponse.questionResponses.map((questionResponse, index) => {
                    const surveyQuestion = survey.surveyQuestions[index];
                    return <Td key={"ResponseQuestion_" + index} style={{ textAlign: "left", paddingLeft: "20px", width: "10%", textTransform: "capitalize" }} fontWeight="300 !important">
                        { handleSurveyTypeIcons(getQuestionSurveyType(survey, surveyQuestion), questionResponse)}
                    </Td>;
                })
            }
                
            
            <Td style={{ textAlign: "left", paddingLeft: "20px", width: "20%" }} fontWeight="300 !important">
                {
                    !surveyUserResponse.questionResponses
                        ?
                        survey.status === SurveyStatusType.COMPLETE ? (<div style={{ fontWeight: "500", color: "#171A22" }}>Not Submitted</div>) : (<div style={{ fontWeight: "500", color: "#171A22" }}>Pending</div>)
                        :
                        <div style={{ fontWeight: "500", color: "#36BE24" }}>Submitted</div>
                }
            </Td>

        </Tr>;
    });

    const getSurveyUserResponses = (surveyUuid, filter) => {
        setIsSurveyResponsesFetched(false);
        let URL = `${API_URL}/survey/company/responsesStats/${surveyUuid}/users`;
        if (filter) {
            URL = URL + "?filter=" + filter;
        }
        axios.get(URL, headers).then(res => {
            const responseData = res.data;
            const usersWithResponses = responseData.filter((responseItem) => 
                responseItem.questionResponses
            );

            const usersWithoutResponses = responseData.filter((responseItem) => 
                !responseItem.questionResponses
            );

            const sortByName = (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());

            const organizedList = [
                ...usersWithResponses.sort(sortByName),
                ...usersWithoutResponses.sort(sortByName),
            ];
            setSurveyUserSubmissionList(organizedList);
        }).finally(() => {
            setIsSurveyResponsesFetched(true);
        }).catch(error => {
            return Toast.error(error.getApiErrorDetails().message);
        });
    };

    const filterUserSurveyResponses = (userGroup) => {
        setSelectedSurveyStatusType(userGroup.value);
        getSurveyUserResponses(survey.uuid, userGroup.value);
    };

    useEffect(() => {
        if (survey) {
            getSurveyUserResponses(survey.uuid, survey.userGroup);
        }
    }, []);

    return (
        <React.Fragment>
            <StandardModal
                open={showDialog}
                onClose={handleClose}
                title="Survey User Submissions"
                widthMode="table"
                bodyPadding="0"
            >
                <Box sx={{ margin: "24px 55px 10px 55px" }}>
                    <FlexContainer direction="row" tDirection="row" mDirection="row" justifyContent="flex-end" >
                        { survey.userGroup === EnumUserGroup.ALL_USERS &&
                            <FlexContainer direction="row" justifyContent="flex-end" width="fit-content">
                                <FlexContainer direction="unset">
                                    <Text size="14px" weight="700" margin="auto 16px auto 0" display="inline-flex">
                                        Filter:
                                    </Text>
                                    <div style={{ width: "270px", margin: "auto 0" }}>
                                        <CustomSelect
                                            options={ surveyUserGroupOptions }
                                            onChange={(choice) => filterUserSurveyResponses(choice)}
                                            value={ surveyUserGroupOptions.find((obj) => obj.value === selectedSurveyStatusType) }
                                            radius="7px"
                                            menuOffset="0"
                                            menuBorder=".5px solid black"
                                            fontWeight={700}
                                        />
                                    </div>
                                </FlexContainer>
                            </FlexContainer>
                        }
                    </FlexContainer>
                </Box>
                <Divider />
                <div style={{ maxWidth: "94.5rem", padding: "30px 55px 0 55px" }}>
                    <FlexContainer direction="column" marginBottom="27px" justifyContent="space-between" wrap="nowrap" width="100%">
                        <div >
                            <FlexContainer direction="column">
                                <Text color="#000" size="20px" align="left" weight="700" margin="0.5rem 0px">
                                    Number of Submitted Surveys:&nbsp;{insights.numberOfTotalSurveysSubmitted}/{insights.numberOfUsersInTargetGroup}
                                </Text>
                                <Text color="#000" size="16px" weight="300" align="left" margin="1rem 0px">
                                    This is a list of all users who have, and have not, submitted their survey responses.
                                </Text>
                            </FlexContainer>
                            <FlexContainer display={survey.surveyQuestions.length > 5 ? "block" : "column"} style={{ columnCount: 2, columnGap: "1rem" }}>
                                {survey.surveyQuestions.map((surveyQuestion, index) => (
                                    <div key={index} style={{ breakInside: "avoid", marginBottom: "1rem" }}>
                                        <Text color="#000" size="16px" align="left" weight="700" margin="0.5rem 0px">
                                            Question&nbsp;{surveyQuestion.questionOrder}: <span style={{ fontWeight: "300" }}>{surveyQuestion.question}</span>
                                        </Text>
                                    </div>
                                ))}
                            </FlexContainer>
                        </div>
                    </FlexContainer>

                    { !isSurveyResponsesFetched &&
                        <div style={{ height: "24rem" }}>
                            <LottieLoadingIndicator/>
                        </div>
                    }
                </div>
                <Divider />
                <div style={{ maxWidth: "94.5rem", margin: "20px 55px 30px 55px" }}>
                    { isSurveyResponsesFetched &&
                        <SurveyScrollableDiv maxWidth="94.5rem" maxHeight="24rem" tableMinWidth="920px" style={{ height: "24rem", borderRadius: "8px" }}>
                            <DataTable minWidth="920px">
                                <thead style={{ width: "100%", position: "sticky", top: "0px", zIndex: "150", backgroundColor: "#fff" }}>
                                    <tr style={{ width: "100%" }}>
                                        <Th style={{ width: "30%", paddingLeft: "20px", paddingBottom: "20px" }}
                                            onClick={() => sortData(InsightSortableColumns.NAME)}
                                        >
                                            Name&nbsp;&nbsp;&nbsp;{renderSortIndicator(InsightSortableColumns.NAME)}
                                        </Th>
                                        <Th style={{ width: "20%", paddingLeft: "20px", paddingBottom: "20px" }}
                                            onClick={() => sortData(InsightSortableColumns.EMAIL)}
                                        >
                                            Email&nbsp;&nbsp;&nbsp;{renderSortIndicator(InsightSortableColumns.EMAIL)}
                                        </Th>
                                        
                                        {survey.surveyQuestions && survey.surveyQuestions.map((surveyQuestion, index) => (
                                            <Th key={index} style={{ width: "10%", paddingLeft: "20px", paddingBottom: "20px" }}
                                                onClick={null} /* TODO: make this work sortData(InsightSortableColumns.QUESTION_BY_INDEX) */ 
                                            >
                                                Question {surveyQuestion.questionOrder} &nbsp;&nbsp;&nbsp; { /*TODO: make this work renderSortIndicator(InsightSortableColumns.QUESTION_BY_INDEX) */ }
                                            </Th>
                                        ))}
                                       
                                        <Th style={{ width: "20%", paddingLeft: "20px", paddingBottom: "20px" }}
                                            onClick={() => sortData(InsightSortableColumns.STATUS)}
                                        >
                                            Status&nbsp;&nbsp;&nbsp;{renderSortIndicator(InsightSortableColumns.STATUS)}
                                        </Th>
                                    </tr>
                                </thead>
                                <tbody style={{ overflow: "auto", height: "auto", borderRadius: "8px" }}>
                                    {surveyUserSubmissionListItems(surveyUserSubmissionList)}
                                    {emptyList(survey, surveyUserSubmissionList.length)}
                                </tbody>
                            </DataTable>
                        </SurveyScrollableDiv>
                    }
                </div>
                <Divider />

                <Box display="flex" justifyContent="center" margin="auto">
                    <Button 
                        border="1px solid #000"
                        color="#000"
                        marginRight="15px"
                        width="140px"
                        onClick={handleClose}
                        style={{ borderRadius: "7px", boxShadow: "none", margin: "30px" }}
                        className="form-btns disabled"
                    >
                        Close
                    </Button>
                </Box>
            </StandardModal>
        </React.Fragment>
    );
}; 

export default SurveyUserSubmissionsModal;
